import angular from "angular";
import { RecordDataSource } from "../../../records/grids";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingServiceName,
} from "../../../records/grids/settings/gridSettingsService";

import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import {
  DoubleClickHandlerService,
  vsName as DoubleClickHandlerServiceName,
  CellDoubleClickedEventHandlerArgs,
} from "../../../records/grids/cells";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { PatchObject, Record } from "../../../records/server/crud";
import { ICrudRecordResponse } from "../../../records/server/query";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";
import { percentageValueGetterFactory } from "../../../records/grids/cells/valueGetters";
import { ColDef } from "../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../permissions";

const progressiveMeterReadingValidationSettingsUrl =
  "/api/machines/progressives/ProgressiveMeterReadingValidationSetting";
const gridId = Symbol("progressiveMeterReadingValidationSettings");

export class ProgressiveMeterReadingValidationSettingViewController {
  static $inject = [
    GridSettingServiceName,
    "$compile",
    DoubleClickHandlerServiceName,
    ServerUtilityName,
  ];

  gridDataSource: RecordDataSource;
  gridSettings: IGridSettings;
  columns: ColDef[];

  constructor(
    gridSettingsService: GridSettingsService,
    $compile: angular.ICompileService,
    doubleClickHandlerService: DoubleClickHandlerService,
    private readonly serverUtility: ServerUtility
  ) {
    this.gridDataSource = new RecordDataSource(
      serverUtility,
      progressiveMeterReadingValidationSettingsUrl,
      true
    );
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.columns = [
      {
        field: "MinimumWarningPercentage",
        headerName: "Minimum Warning Percentage",
        filter: new FilterProvider("MinimumWarningPercentage", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 0 }),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simplePercentageField({
                key: "MinimumWarningPercentage",
                name: "MinimumWarningPercentage",
                label: "Minimum Warning Percentage",
                min: 0,
                max: 100,
                step: 1,
              }),
              type: "gridInput",
            },
          },
          getRecordName: () => this.getRecordName(),
        }),
      },
      {
        field: "MaximumWarningPercentage",
        headerName: "Maximum Warning Percentage",
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 0 }),
        filter: new FilterProvider("MaximumWarningPercentage", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              ...fieldHelpers.simplePercentageField({
                key: "MaximumWarningPercentage",
                name: "MaximumWarningPercentage",
                label: "Maximum Warning Percentage",
                min: 100,
                step: 1,
              }),
              type: "gridInput",
            },
          },
          getRecordName: () => this.getRecordName(),
        }),
      },
      {
        field: "ReadingBelowThresholdAction",
        headerName: "Reading Below Threshold Action",
        filter: new FilterProvider("ReadingBelowThresholdAction", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                {
                  display: "None",
                  value: "None",
                },
                {
                  display: "Warning",
                  value: "Warning",
                },
                {
                  display: "Error",
                  value: "Error",
                },
              ],
              queryFieldNames: ["ReadingBelowThresholdAction"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: {
              key: "ReadingBelowThresholdAction",
              name: "ReadingBelowThresholdAction",
              type: "gridSelect",
              templateOptions: {
                label: "Reading Below Threshold Action",
                options: [
                  {
                    display: "None",
                    value: "None",
                  },
                  {
                    display: "Warning",
                    value: "Warning",
                  },
                  {
                    display: "Error",
                    value: "Error",
                  },
                ],
                required: true,
                labelProp: "display",
                valueProp: "value",
              },
            },
          },
          getRecordName: () => this.getRecordName(),
        }),
      },
    ];
  }

  private getRecordName(): string {
    return "Validation Settings";
  }

  private patchRecord(
    patchObject: PatchObject
  ): angular.IPromise<ICrudRecordResponse<Record>> {
    return this.serverUtility.patch({
      url: progressiveMeterReadingValidationSettingsUrl,
      patchObject,
    });
  }

  private fetchRecord(
    args: CellDoubleClickedEventHandlerArgs
  ): angular.IPromise<Record | undefined> {
    return this.serverUtility.querySingleWrapped({
      url: progressiveMeterReadingValidationSettingsUrl,
      query: { Id: args.data.Id },
    });
  }
}
